'use client';

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Language, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, getLanguageByCode } from './languages';
import { detectBrowserLanguage } from './detectLanguage';
import { saveLanguagePreference, loadLanguagePreference } from './storage';

interface LanguageContextType {
  currentLanguage: Language;
  changeLanguage: (languageCode: string) => void;
  isLoading: boolean;
  supportedLanguages: Language[];
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: string;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ 
  children, 
  defaultLanguage = DEFAULT_LANGUAGE 
}) => {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(
    getLanguageByCode(defaultLanguage) || SUPPORTED_LANGUAGES[0]
  );
  const [isLoading, setIsLoading] = useState(true);

  // 初始化语言设置
  useEffect(() => {
    const initializeLanguage = () => {
      try {
        // 1. 尝试从localStorage获取用户偏好
        const savedLanguage = loadLanguagePreference();
        if (savedLanguage) {
          const language = getLanguageByCode(savedLanguage);
          if (language) {
            setCurrentLanguage(language);
            setIsLoading(false);
            return;
          }
        }

        // 2. 检测浏览器语言 (暂时屏蔽)
        // const detectedLanguage = detectBrowserLanguage();
        // if (detectedLanguage) {
        //   const language = getLanguageByCode(detectedLanguage);
        //   if (language) {
        //     setCurrentLanguage(language);
        //     saveLanguagePreference(detectedLanguage);
        //     setIsLoading(false);
        //     return;
        //   }
        // }

        // 3. 使用默认语言
        setCurrentLanguage(getLanguageByCode(defaultLanguage) || SUPPORTED_LANGUAGES[0]);
        setIsLoading(false);
      } catch (error) {
        console.warn('Failed to initialize language:', error);
        setCurrentLanguage(getLanguageByCode(defaultLanguage) || SUPPORTED_LANGUAGES[0]);
        setIsLoading(false);
      }
    };

    initializeLanguage();
  }, [defaultLanguage]);

  const changeLanguage = (languageCode: string) => {
    const language = getLanguageByCode(languageCode);
    if (language) {
      setCurrentLanguage(language);
      saveLanguagePreference(languageCode);
      
      // 设置HTML lang属性
      if (typeof document !== 'undefined') {
        document.documentElement.lang = languageCode;
        document.documentElement.dir = language.rtl ? 'rtl' : 'ltr';
      }
    } else {
      console.warn(`Unsupported language code: ${languageCode}`);
    }
  };

  const contextValue: LanguageContextType = {
    currentLanguage,
    changeLanguage,
    isLoading,
    supportedLanguages: SUPPORTED_LANGUAGES,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};